<script setup>
import { computed, onBeforeUnmount, onMounted, ref, defineProps, defineEmits, getCurrentInstance } from "vue";
import { useStore } from "vuex";
const props = defineProps({
  name: {
    type: String,
    default: null,
  },
  id: {
    type: String,
    default: null,
  },
  autocomplete: {
    type: String,
    default: null,
  },
  placeholder: {
    type: String,
    default: null,
  },
  options: {
    type: Array,
    default: null,
  },
  type: {
    type: String,
    default: "text",
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  max: {
    type: String,
    default: null,
  },
  min: {
    type: String,
    default: null,
  },
  modelValue: {
    type: [String, Number, Boolean, Array, Object],
    default: "",
  },
  height: {
    type: String,
    default: null
  },
  icon: {
    type: String,
    default: null
  },
  icon2: {
    type: String,
    default: null
  },
  required: Boolean,
  borderless: Boolean,
  transparent: Boolean,
  ctrlKFocus: Boolean,
});

const emit = defineEmits(["update:modelValue", "right-icon-click","update:changeDate"]);
const instance = getCurrentInstance();
const computedValue = computed({
  get: () => props.modelValue,
  set: (value) => {
    emit("update:modelValue", value);
  },
});

const inputElClass = computed(() => {
  const base = [
    "px-2 py-1 max-w-full focus:ring focus:outline-none dark:border-gray-700 rounded w-full",
    "dark:placeholder-gray-400 text-xs",
    computedType.value === "textarea" ? "h-20" : "h-10",
    props.borderless ? "border-0" : "border",
    props.transparent ? "bg-transparent" : "bg-white dark:bg-gray-800",
    props.disabled ? "border-gray-300 cursor-not-allowed select-none bg-slate-300" : "border-blue-300",
    props.height ? props.height : ""
  ];

  return base;
});

const computedType = computed(() => (props.options ? "select" : props.type));

const store = useStore();

const inputEl = ref(null);

if (props.ctrlKFocus) {
  const fieldFocusHook = (e) => {
    if (e.ctrlKey && e.key === "k") {
      e.preventDefault();
      inputEl.value.focus();
    } else if (e.key === "Escape") {
      inputEl.value.blur();
    }
  };



  onMounted(() => {
    if (!store.state.isFieldFocusRegistered) {
      window.addEventListener("keydown", fieldFocusHook);

      store.commit("basic", {
        key: "isFieldFocusRegistered",
        value: true,
      });
    }
  });

  onBeforeUnmount(() => {
    window.removeEventListener("keydown", fieldFocusHook);

    store.commit("basic", {
      key: "isFieldFocusRegistered",
      value: false,
    });
  });
}

const onDateChange = (data) => {
  let dis=!props.disabled
  if (data===1) {
    instance.emit("update:changeDate", {
      disabled: dis,
      remove:false
    });
  }else if(data===2){
    instance.emit("update:changeDate", {
      disabled:props.disabled,
      remove:props.disabled? false:true
    });
  }
};
</script>

<template>
  <div class="relative">
    <select v-if="computedType === 'select'" :id="id" v-model="computedValue" :name="name" :class="inputElClass"
      :disabled="disabled">
      <option v-for="option in options" :key="option.id ?? option" :value="option">
        {{ option.label ?? option.name }}
      </option>
    </select>
    <textarea v-else-if="computedType === 'textarea'" :id="id" v-model="computedValue" :class="inputElClass" :name="name"
      :placeholder="placeholder" :required="required" :disabled="disabled" />
    <input v-else :id="id" ref="inputEl" v-model="computedValue" :name="name" :autocomplete="autocomplete"
      :required="required" :placeholder="placeholder" :type="computedType" :class="inputElClass" :disabled="disabled"
      :max="max" :min="min" />
    <span class="absolute right-1 cursor-pointer top-2" v-if="icon"><base-icon :path="icon" :color="props.disabled?'':'text-green-500'" @click="onDateChange(1)"/></span>
    <span class="absolute right-8 cursor-pointer top-2" v-if="icon2 && !props.disabled"><base-icon :path="icon2" color="text-red-500" @click="onDateChange(2)" /></span>
  </div>
</template>
